<template lang="pug">
#app
  router-view(v-show="loaded")

  PcOkModal(v-model="showStatus502ModalForPc" :title="status502ModalTitle" :message="status502ModalMessage")
  MobileOkModal(v-model="showStatus502ModalForMobile" :title="status502ModalTitle" :message="status502ModalMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { commonStore } from "@/store/store";
import { userStore } from "@/store/store";
import SystemSettingsService, { SystemSetting } from "@/commons/services/SystemSettings";
import SendSystemErrorService from "@/commons/services/SendSystemError";
import Setting from "../public/setting.json";
import SystemError from "./commons/models/SystemError";

import PcOkModal from "@/pc/basics/OkModal.vue";
import MobileOkModal from "@/mobile/basics/OkModal.vue";

@Component({
  components: {
    PcOkModal,
    MobileOkModal
  }
})
export default class App extends Vue {
  /** システム設定サービス */
  @Inject("SystemSettingsService") systemSettingsService!: SystemSettingsService;
  /** システムエラーサービス */
  @Inject("SendSystemErrorService") sendSystemErrorService!: SendSystemErrorService;

  /**
   * 画面表示用フラグ
   * アプリバージョンチェック時のブラウザ更新で画面チラつきが発生するので
   * v-cloakではなくフラグで表示を管理する
   */
  loaded: boolean = false;

  /** 502エラー時のモーダル表示可否（PC） */
  showStatus502ModalForPc: boolean = false;
  /** 502エラー時のモーダル表示可否（mobile） */
  showStatus502ModalForMobile: boolean = false;
  /** 502エラーのモーダルタイトル */
  status502ModalTitle: string = "サーバーでエラーが発生しました";
  /** 502エラーのモーダルメッセージ */
  status502ModalMessage: string = "ご迷惑をおかけして申し訳ありません。\nしばらくしてから再度お試しください。\nそれでもエラーになる場合は、お問い合わせください。";

  /**
   * vue error handler
   *
   * - 最も親コンポーネントであるapp.vueでエラーを一度ハンドリングする
   * - 502エラーの場合、システムエラーにせずにモーダルを表示する（errorCollectionsにはデータ登録する）
   *
   * @param error エラー内容
   * @param vm 例外が発生したvueコンポーネント
   * @param info Vue 固有のエラー情報
   */
  errorCaptured(error: any, vm: Vue, info: any): boolean {
    const systemError: SystemError = error;
    switch (systemError.statusCode) {
      case 502: {
        // モーダルを表示する（PC）
        if (commonStore.appmode == "pc") {
          this.showStatus502ModalForPc = true;
        }
        // モーダルを表示する（モバイル）
        else {
          this.showStatus502ModalForMobile = true;
        }

        // errorCollectionsTBLにエラー内容を登録する
        // 複数回エラーハンドラーが呼ばれた場合、facilityId=0になっているのでその場合は何もしない
        if (0 < userStore.loginUser.facilityId) {
          const sendSystemErrorService: SendSystemErrorService = new SendSystemErrorService();
          sendSystemErrorService.post(error, userStore, vm.$router, info);
        }
        return false; // 502の場合のみここでエラーの伝搬を止める
      }
    }
    return true;
  }

  /**
   * vue lifecycle created
   */
  public async created(): Promise<void> {
    await this.checkAppVersion();
    return;
  }

  /**
   * ### アプリバージョンチェック
   * - localstorageのアプリバージョン取得
   * - バージョン不一致の場合、現在のバージョンを書き込み
   * - バージョンを書き込んだ場合はブラウザのreloadを実施する
   */
  public async checkAppVersion(): Promise<void> {
    const version = localStorage.getItem("version");
    const systemSetting: SystemSetting = await this.systemSettingsService.getSystemSetting();

    if (version != systemSetting.version) {
      localStorage.setItem("version", systemSetting.version);
      alert("システムがバージョンアップされました。\nキーボードのF5ボタンもしくはブラウザの更新ボタンでページを更新してお試しください。");
      location.reload();
    } else {
      // アプリバージョンチェックでリロードしない場合は画面表示を開始する
      this.loaded = true;
    }

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public mounted(): void {
    // アプリモードをstoreに保持させる
    const appmode: string = this.getAppmode();
    commonStore.setAppmode({ appmode: appmode });

    console.info("appmode: ", appmode);

    // アプリモードに応じてCSSの読込みを分岐する
    // PC版
    if (appmode == "pc") {
      // 共通スタイル
      require("@/styles/pc/admin.scss");
      require("@/styles/pc/modal.scss");
      require("@/styles/pc/common.scss");

      // BOOTSTRAP、BOOTSTRAP VUEのCSSカスタマイズ
      require("@/styles/pc/bootstrap/bootstrap.scss");
    }
    // モバイル版
    else if (appmode == "mobile") {
      require("@/styles/mobile/modal.scss");
      require("@/styles/mobile/common.scss");

      // BOOTSTRAP、BOOTSTRAP VUEのCSSカスタマイズ
      require("@/styles/mobile/bootstrap/bootstrap.scss");
    }

    return;
  }

  /**
   * ### useragentからアプリモードを返す
   *
   * - モバイルでなければpcを返す
   */
  public getAppmode(): string {
    const userAgent = window.navigator.userAgent;
    const mobileAgent: Array<string> = Setting.mobileAgent;

    // mobileという文字列が入っていたらモバイルとして扱いたいが、iPadはPC扱いにしたいので個々のスマートフォンのagentで検索する
    for (const agent of mobileAgent) {
      if (userAgent.indexOf(agent) != -1) {
        return "mobile";
      }
    }

    return "pc";
  }
}
</script>
